import React, { useState } from "react";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const askBrowserNotifications = async () => {
  if (!("Notification" in window)) return;
  if (Notification.permission === "default") await Notification.requestPermission();
};

const AlertSettings = () => {
  const { user, refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const alertsOn = user?.parcel_alerts_enabled !== false;
  const shareOn = !!user?.location_sharing_enabled;

  const patch = async (body, msg) => {
    setBusy(true);
    try { await api.patch("/users/me", body); await refresh(); toast.success(msg); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save."); }
    setBusy(false);
  };

  const toggleShare = async (v) => {
    if (!v) return patch({ location_sharing_enabled: false }, "Live location turned off");
    if (!navigator.geolocation) return toast.error("Your browser doesn't support location. Route-based alerts still work.");
    navigator.geolocation.getCurrentPosition(
      () => patch({ location_sharing_enabled: true }, "Live location on — only while you have an active trip"),
      () => toast.error("Location permission was refused. We'll keep using your posted trip route for alerts."),
      { enableHighAccuracy: false, timeout: 15000, maximumAge: 600000 },
    );
  };

  const deleteHistory = async () => {
    setBusy(true);
    try { const r = await api.delete("/users/me/location-history"); toast.success(`Location history deleted (${r.data.deleted} points)`); await refresh(); }
    catch { toast.error("Couldn't delete location history."); }
    setBusy(false);
  };

  return (
    <div className="space-y-4" data-testid="alert-settings">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-medium">Parcel alerts near my route</div>
          <div className="text-[11px] text-slate-500">Get notified when a new parcel's pickup is near an active trip of yours.</div>
        </div>
        <Switch data-testid="parcel-alerts-toggle" checked={alertsOn} disabled={busy}
                onCheckedChange={(v) => { if (v) askBrowserNotifications(); patch({ parcel_alerts_enabled: v }, v ? "Parcel alerts on" : "Parcel alerts off"); }}/>
      </div>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-medium">Use my live location (optional)</div>
          <div className="text-[11px] text-slate-500">
            Helps us alert you to parcels near where you actually are. We only check an approximate location (about 1 km),
            every 10 minutes, and only while you have an active trip. Off by default.
          </div>
        </div>
        <Switch data-testid="location-sharing-toggle" checked={shareOn} disabled={busy} onCheckedChange={toggleShare}/>
      </div>
      <Button variant="outline" size="sm" className="rounded-full" onClick={deleteHistory} disabled={busy} data-testid="delete-location-history-btn">
        Delete my location history
      </Button>
    </div>
  );
};

export default AlertSettings;
